import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CreditCard, CheckCircle, CircleNotch, Receipt, Bank } from "@phosphor-icons/react";
import { fmtMoney } from "@/components/PageHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicInvoicePage() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/invoices/${token}`)
      .then((r) => setInv(r.data))
      .catch((e) =>
        setErr(e.response?.data?.detail || "Invoice not found or link expired")
      );
  }, [token]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/public/invoices/${token}/checkout`,
        { origin_url: window.location.origin }
      );
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not start payment");
      setBusy(false);
    }
  };

  const payWithPaystack = async () => {
    const email = window.prompt("Your email (for Paystack receipt):", "");
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/public/invoices/${token}/paystack/init`,
        { origin_url: window.location.origin, email }
      );
      window.location.href = data.authorization_url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not start Paystack payment");
      setBusy(false);
    }
  };

  const paystackSupported = inv &&
    ["NGN", "USD", "GHS", "ZAR", "KES"].includes(inv.currency);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-md w-full border border-border bg-card p-10 text-center">
          <h1 className="font-serif-display text-3xl">Not available</h1>
          <p className="text-sm text-muted-foreground mt-2">{err}</p>
        </div>
      </div>
    );
  }
  if (!inv) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  const paid = inv.payment_status === "paid";

  return (
    <div className="min-h-screen bg-background py-16 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div className="font-serif-display text-2xl tracking-tight">
            LedgerLoom
          </div>
          <div className="overline">Secure Invoice</div>
        </div>

        <div className="border border-border bg-card">
          <div className="px-8 py-8 border-b border-border">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="overline mb-2">From</div>
                <div className="font-serif-display text-2xl">
                  {inv.company_name}
                </div>
              </div>
              <div className="text-right">
                <div className="overline mb-2">Invoice</div>
                <div className="font-mono text-sm">
                  #{inv.invoice_number || token.slice(0, 8).toUpperCase()}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-6 text-sm">
              <div>
                <div className="overline mb-1">Bill To</div>
                <div>{inv.counterparty}</div>
              </div>
              <div>
                <div className="overline mb-1">Issued</div>
                <div className="num">{inv.date}</div>
              </div>
              <div>
                <div className="overline mb-1">Due</div>
                <div className="num">{inv.due_date || "On receipt"}</div>
              </div>
            </div>
          </div>

          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left">
                <th className="px-6 py-3 overline">Description</th>
                <th className="px-6 py-3 overline text-right">Qty</th>
                <th className="px-6 py-3 overline text-right">Price</th>
                <th className="px-6 py-3 overline text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {inv.lines.map((l, i) => (
                <tr key={i} className="border-b border-border last:border-b-0">
                  <td className="px-6 py-4">{l.description || "—"}</td>
                  <td className="px-6 py-4 text-right num">{l.quantity}</td>
                  <td className="px-6 py-4 text-right num">
                    {fmtMoney(l.unit_price, inv.currency)}
                  </td>
                  <td className="px-6 py-4 text-right num">
                    {fmtMoney(l.quantity * l.unit_price, inv.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="px-8 py-6 border-t border-border space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="num">{fmtMoney(inv.subtotal, inv.currency)}</span>
            </div>
            {inv.tax_amount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax</span>
                <span className="num">
                  {fmtMoney(inv.tax_amount, inv.currency)}
                </span>
              </div>
            )}
            <div className="flex justify-between items-baseline pt-3 border-t border-border">
              <span className="font-serif-display text-xl">Total Due</span>
              <span className="font-serif-display text-3xl num">
                {fmtMoney(inv.total, inv.currency)}
              </span>
            </div>
          </div>

          {inv.notes && (
            <div className="px-8 py-4 border-t border-border text-sm text-muted-foreground">
              <div className="overline mb-1">Notes</div>
              {inv.notes}
            </div>
          )}

          <div className="px-8 py-6 border-t-2 border-primary bg-muted/30">
            {paid ? (
              <div className="flex items-center justify-center gap-2 text-primary font-mono uppercase tracking-widest text-sm">
                <CheckCircle size={20} weight="fill" />
                Paid in Full
              </div>
            ) : (
              <div className="space-y-2">
                <Button
                  onClick={pay}
                  disabled={busy}
                  data-testid="public-pay-btn"
                  className="w-full h-12 rounded-sm font-mono uppercase tracking-widest text-xs"
                >
                  <CreditCard size={16} className="mr-2" />
                  {busy ? "Redirecting..." : `Pay ${fmtMoney(inv.total, inv.currency)} with Card (Stripe)`}
                </Button>
                {paystackSupported && (
                  <Button
                    onClick={payWithPaystack}
                    disabled={busy}
                    variant="outline"
                    data-testid="public-paystack-btn"
                    className="w-full h-12 rounded-sm font-mono uppercase tracking-widest text-xs"
                  >
                    <Bank size={16} className="mr-2" />
                    Pay with Paystack
                  </Button>
                )}
              </div>
            )}
            <div className="mt-3 text-center text-[10px] uppercase tracking-widest text-muted-foreground">
              <Receipt size={10} className="inline mr-1" />
              Payment processed by Stripe · No card data stored on this site
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
